import { useState, useEffect } from "react";
import { avatarBg } from "@/lib/avatar";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Shield, Trash2, Users, Search, X, DollarSign, RefreshCw, CheckCircle2, LifeBuoy, ShieldAlert, MessageCircleHeart } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import CompanionInvitesAdmin from "@/components/CompanionInvitesAdmin";
import { CompanionAccountsAdmin, SocialInvitesAdmin } from "@/components/CompanionAdminTools";
import AdminRewards from "@/components/AdminRewards";
import AdminRefunds from "@/components/AdminRefunds";
import AdminEvents from "@/components/AdminEvents";
import AdminCompanionInbox from "@/components/AdminCompanionInbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export default function Admin() {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, male: 0, female: 0 });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [payoutActionId, setPayoutActionId] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [modLogs, setModLogs] = useState([]);
  const [resolveDialog, setResolveDialog] = useState({ open: false, ticket: null });
  const [resolutionNote, setResolutionNote] = useState("");
  const [resolvingTicket, setResolvingTicket] = useState(false);
  const [nudgeStats, setNudgeStats] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post(`/admin/login?password=${password}`);
      setAuthenticated(true);
      toast.success("Admin authenticated");
      fetchUsers();
      fetchPendingPayouts();
      fetchTickets();
      fetchModLogs();
      fetchNudgeStats();
    } catch (error) {
      toast.error("Invalid admin password");
    } finally {
      setLoading(false);
    }
  };

  const fetchNudgeStats = async () => {
    try {
      const response = await api.get(`/admin/nudge-analytics?admin_password=${password}`);
      setNudgeStats(response.data);
    } catch (error) {
      console.error("Failed to fetch nudge analytics:", error);
    }
  };

  const fetchModLogs = async () => {
    try {
      const response = await api.get(`/admin/moderation-log?admin_password=${password}`);
      setModLogs(response.data.logs);
    } catch (error) {
      console.error("Failed to fetch moderation log:", error);
    }
  };

  const fetchTickets = async () => {
    try {
      const response = await api.get(`/admin/support-tickets?admin_password=${password}`);
      setTickets(response.data.tickets);
    } catch (error) {
      console.error("Failed to fetch support tickets:", error);
    }
  };

  const handleResolveTicket = async () => {
    const t = resolveDialog.ticket;
    if (!t) return;
    setResolvingTicket(true);
    try {
      await api.post(`/admin/support-tickets/${t.ticket_id}/resolve?admin_password=${password}`, {
        resolution: resolutionNote.trim(),
      });
      toast.success("Ticket resolved — the member has been notified");
      setResolveDialog({ open: false, ticket: null });
      setResolutionNote("");
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resolve ticket");
    } finally {
      setResolvingTicket(false);
    }
  };

  const fetchPendingPayouts = async () => {
    try {
      const response = await api.get(`/admin/pending-payouts?admin_password=${password}`);
      setPendingPayouts(response.data.pending_payouts);
    } catch (error) {
      console.error("Failed to fetch pending payouts:", error);
    }
  };

  const handleRetryPayout = async (offerId) => {
    setPayoutActionId(offerId);
    try {
      const response = await api.post(`/admin/payouts/${offerId}/retry?admin_password=${password}`);
      toast.success(`Payout sent automatically: $${response.data.amount} → ${response.data.recipient}`);
      fetchPendingPayouts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Automatic payout failed - PayPal may still not have Payouts approved");
    } finally {
      setPayoutActionId(null);
    }
  };

  const handleMarkPaid = async (offerId) => {
    if (!confirm("Confirm you have manually sent this payment via PayPal?")) return;
    setPayoutActionId(offerId);
    try {
      const response = await api.post(`/admin/payouts/${offerId}/mark-paid?admin_password=${password}`);
      toast.success(`Marked as paid: $${response.data.amount} → ${response.data.recipient}`);
      fetchPendingPayouts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as paid");
    } finally {
      setPayoutActionId(null);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get(`/admin/users?admin_password=${password}`);
      setUsers(response.data.users);
      setStats({
        total: response.data.total,
        male: response.data.male,
        female: response.data.female,
      });
    } catch (error) {
      toast.error("Failed to fetch users");
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await api.delete(`/admin/users/${userToDelete.user_id}?admin_password=${password}`);
      toast.success(`Deleted ${userToDelete.name}`);
      setDeleteDialog(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) {
      toast.error("No users selected");
      return;
    }

    if (!confirm(`Delete ${selectedUsers.length} users?`)) return;

    try {
      await api.post(
        `/admin/users/bulk-delete?admin_password=${password}`,
        selectedUsers
      );
      toast.success(`Deleted ${selectedUsers.length} users`);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete users");
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleSelectUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map((u) => u.user_id));
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="glass-card p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full gradient-neon flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold font-['Outfit'] mb-2">
                Admin Panel
              </h1>
              <p className="text-muted-foreground">Enter admin password</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Admin Password"
                className="bg-white/5 border-white/10"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password-input"
              />
              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading ? "Authenticating..." : "Login"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold font-['Outfit'] flex items-center gap-3">
              <Shield className="w-8 h-8 text-primary" />
              Admin Panel
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage users and platform data
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              setAuthenticated(false);
              setPassword("");
            }}
            className="border-white/10"
          >
            Logout
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="glass-card p-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{stats.male}</p>
                <p className="text-sm text-muted-foreground">Male Users</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-pink-500" />
              <div>
                <p className="text-2xl font-bold">{stats.female}</p>
                <p className="text-sm text-muted-foreground">Female Users</p>
              </div>
            </div>
          </div>
        </div>

        {/* Nudge Analytics */}
        {nudgeStats && (
          <div className="glass-card p-6 mb-6 border border-rose-500/30" data-testid="nudge-analytics-section">
            <div className="flex items-center gap-3 mb-4">
              <MessageCircleHeart className="w-6 h-6 text-rose-400" />
              <h2 className="text-lg font-semibold">Win-Back Nudge Analytics</h2>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="p-3 rounded-lg bg-white/5 text-center">
                <p className="text-2xl font-bold" data-testid="nudge-total">{nudgeStats.total_nudges}</p>
                <p className="text-xs text-muted-foreground">Nudges sent</p>
              </div>
              <div className="p-3 rounded-lg bg-white/5 text-center">
                <p className="text-2xl font-bold text-green-400" data-testid="nudge-replied">{nudgeStats.replied}</p>
                <p className="text-xs text-muted-foreground">Replied after nudge</p>
              </div>
              <div className="p-3 rounded-lg bg-white/5 text-center">
                <p className="text-2xl font-bold text-rose-400" data-testid="nudge-conversion">{nudgeStats.conversion_rate}%</p>
                <p className="text-xs text-muted-foreground">Conversion</p>
              </div>
            </div>
            {nudgeStats.nudged_users?.length > 0 ? (
              <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="nudged-users-list">
                {nudgeStats.nudged_users.map((u) => (
                  <div key={u.user_id} className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-white/5" data-testid={`nudged-user-${u.user_id}`}>
                    <div className="min-w-0">
                      <span className="font-medium">{u.name || "Member"}</span>
                      <span className="text-xs text-muted-foreground ml-2 truncate">{u.email}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-muted-foreground">{u.nudged_at ? new Date(u.nudged_at).toLocaleDateString() : ""}</span>
                      {u.replied ? (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/15 text-green-400 border border-green-500/30">✓ Replied</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-white/5 text-muted-foreground border border-white/10">Silent</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="nudge-empty">No win-back nudges sent yet — they fire automatically 24h after an unanswered companion teaser.</p>
            )}
          </div>
        )}

        {/* Events: create, attendees, cancel */}
        <AdminEvents password={password} />

        {/* AI Companion Mystery Invites */}
        <CompanionInvitesAdmin password={password} />

        {/* AI Companion Accounts (login as) */}
        <CompanionAccountsAdmin password={password} />

        {/* Companion Inbox: all member <-> AI conversations */}
        <AdminCompanionInbox password={password} />

        {/* Social Secret Admirer queue */}
        <SocialInvitesAdmin password={password} />

        {/* Rewards: credits, free slots, promo codes */}
        <AdminRewards password={password} />

        {/* Refunds: transaction history + AI-automated processing */}
        <AdminRefunds password={password} />

        {/* Pending Payouts */}
        {pendingPayouts.length > 0 && (
          <div className="glass-card p-6 mb-6 border border-amber-500/40" data-testid="pending-payouts-section">
            <div className="flex items-center gap-3 mb-4">
              <DollarSign className="w-6 h-6 text-amber-400" />
              <h2 className="text-lg font-semibold">
                Pending Payouts ({pendingPayouts.length})
              </h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              These dates were confirmed but the automatic PayPal payout failed. Send the money manually
              via PayPal (Send &amp; Request) then click "Mark as Paid", or click "Retry Auto" once PayPal
              approves Payouts for your account.
            </p>
            <div className="space-y-3">
              {pendingPayouts.map((p) => (
                <div
                  key={p.offer_id}
                  className="flex flex-col md:flex-row md:items-center gap-3 p-4 rounded-lg bg-white/5 border border-white/10"
                  data-testid={`pending-payout-${p.offer_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">
                      {p.woman_name} — <span className="text-amber-400 font-bold">${p.amount?.toFixed(2)}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      PayPal: <span className="text-white/90 select-all">{p.paypal_email || "NOT SET"}</span>
                      {p.confirmed_at && ` • Confirmed ${new Date(p.confirmed_at).toLocaleString()}`}
                    </p>
                    {p.payout_error && (
                      <p className="text-[11px] text-red-400/80 mt-1 truncate">Error: {p.payout_error}</p>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-white/10"
                      disabled={payoutActionId === p.offer_id}
                      onClick={() => handleRetryPayout(p.offer_id)}
                      data-testid={`retry-payout-${p.offer_id}`}
                    >
                      <RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry Auto
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      disabled={payoutActionId === p.offer_id}
                      onClick={() => handleMarkPaid(p.offer_id)}
                      data-testid={`mark-paid-${p.offer_id}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark as Paid
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Support Tickets */}
        {tickets.length > 0 && (
          <div className="glass-card p-6 mb-6 border border-violet-500/40" data-testid="support-tickets-section">
            <div className="flex items-center gap-3 mb-4">
              <LifeBuoy className="w-6 h-6 text-violet-400" />
              <h2 className="text-lg font-semibold">
                Support Tickets ({tickets.filter((t) => t.status === "open").length} open)
              </h2>
            </div>
            <div className="space-y-3">
              {tickets.map((t) => (
                <div
                  key={t.ticket_id}
                  className="p-4 rounded-lg bg-white/5 border border-white/10"
                  data-testid={`ticket-${t.ticket_id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                            t.type === "refund" ? "bg-amber-500/20 text-amber-400" : "bg-red-500/20 text-red-400"
                          }`}
                        >
                          {t.type === "refund" ? "REFUND" : "COMPLAINT"}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                            t.status === "open" ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"
                          }`}
                        >
                          {t.status.toUpperCase()}
                        </span>
                        <p className="font-medium">{t.subject}</p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t.user_name} (<span className="select-all">{t.user_email}</span>) •{" "}
                        {new Date(t.created_at).toLocaleString()}
                      </p>
                      <p className="text-sm text-white/80 mt-2 whitespace-pre-wrap">{t.message}</p>
                      {t.resolution && (
                        <p className="text-xs text-green-400/80 mt-2">Resolution: {t.resolution}</p>
                      )}
                    </div>
                    {t.status === "open" && (
                      <Button
                        size="sm"
                        className="bg-violet-600 hover:bg-violet-700 shrink-0"
                        onClick={() => setResolveDialog({ open: true, ticket: t })}
                        data-testid={`resolve-ticket-${t.ticket_id}`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Resolve
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Moderation Log */}
        {modLogs.length > 0 && (
          <div className="glass-card p-6 mb-6 border border-red-500/30" data-testid="moderation-log-section">
            <div className="flex items-center gap-3 mb-4">
              <ShieldAlert className="w-6 h-6 text-red-400" />
              <h2 className="text-lg font-semibold">Moderation Log — Rejected Uploads ({modLogs.length})</h2>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {modLogs.map((l) => (
                <div key={l.log_id} className="p-3 rounded-lg bg-white/5 border border-white/10 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`modlog-${l.log_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${l.media_type === "video" ? "bg-blue-500/20 text-blue-400" : "bg-orange-500/20 text-orange-400"}`}>
                        {l.media_type.toUpperCase()}
                      </span>
                      <span className="font-medium text-sm">{l.user_name}</span>
                      <span className="text-xs text-muted-foreground">({l.user_email})</span>
                      {l.user_total_rejections > 1 && (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-red-500/25 text-red-400" data-testid={`modlog-repeat-${l.log_id}`}>
                          ⚠ {l.user_total_rejections}x offender
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-white/70 mt-1">{l.reason}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{new Date(l.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search and Actions */}
        <div className="glass-card p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search users by name or email..."
                className="pl-10 bg-white/5 border-white/10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={selectAll}
                className="border-white/10"
              >
                {selectedUsers.length === filteredUsers.length
                  ? "Deselect All"
                  : "Select All"}
              </Button>
              {selectedUsers.length > 0 && (
                <Button
                  variant="destructive"
                  onClick={handleBulkDelete}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete ({selectedUsers.length})
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5 border-b border-white/10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                      onChange={selectAll}
                      className="rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    Gender
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    Credits
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-muted-foreground">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr
                      key={user.user_id}
                      className="hover:bg-white/5 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.user_id)}
                          onChange={() => toggleSelectUser(user.user_id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={
                              user.photos?.[0] ||
                              user.picture ||
                              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                user.name || "User"
                              )}&background=${avatarBg()}&color=fff`
                            }
                            alt={user.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <span className="font-medium">{user.name || "N/A"}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">
                        {user.email || "N/A"}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.gender === "male"
                              ? "bg-blue-500/20 text-blue-400"
                              : user.gender === "female"
                              ? "bg-pink-500/20 text-pink-400"
                              : "bg-gray-500/20 text-gray-400"
                          }`}
                        >
                          {user.gender || "N/A"}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-medium">
                        {user.credits || 0} credits
                      </td>
                      <td className="px-6 py-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setUserToDelete(user);
                            setDeleteDialog(true);
                          }}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Resolve Ticket Dialog */}
      <Dialog open={resolveDialog.open} onOpenChange={(o) => setResolveDialog({ open: o, ticket: o ? resolveDialog.ticket : null })}>
        <DialogContent className="bg-card border-white/10" data-testid="resolve-ticket-dialog">
          <DialogHeader>
            <DialogTitle>Resolve Ticket</DialogTitle>
            <DialogDescription>
              "{resolveDialog.ticket?.subject}" from {resolveDialog.ticket?.user_name}. Add an optional response —
              the member gets it in their notification and email.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="e.g. Your refund of $9.99 has been processed back to your PayPal account."
            className="bg-white/5 border-white/10 min-h-[90px]"
            value={resolutionNote}
            onChange={(e) => setResolutionNote(e.target.value)}
            data-testid="resolution-note-input"
          />
          <div className="flex gap-3 justify-end mt-2">
            <Button variant="outline" onClick={() => setResolveDialog({ open: false, ticket: null })}>
              Cancel
            </Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700"
              onClick={handleResolveTicket}
              disabled={resolvingTicket}
              data-testid="confirm-resolve-ticket-btn"
            >
              {resolvingTicket ? "Resolving..." : "Resolve & Notify"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.name}</strong>?
              This will permanently delete their profile, photos, messages, and all
              associated data.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 justify-end mt-4">
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser}>
              Delete User
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
